import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit"
import { RootState } from "../store"
import BaseApi from "../../api/api"
import { STATUS } from "../../utils/const"

export type DailyPoint = {
  date: string
  revenue: number // net: продажи − возвраты
  payout: number // net: продажи − возвраты + компенсации
  payoutSales: number // gross: ppvz_for_pay только «Продажа»
  returnPayout: number // удержано за возвраты
  compensationPayout: number // компенсации/корректировки
  cost: number
  profit: number
  netProfit: number
  commission: number
  logistics: number
  storage: number
  penalty: number
  deduction: number
  acceptance: number
  acquiring: number
  rebillLogistic: number
  additionalPayment: number
  salesCount: number
  returnsCount: number
}

export type AbcItem = {
  article: string
  name: string
  nmId: number
  revenue: number
  payout: number
  cost: number
  profit: number
  margin: number
  salesCount: number
  grade: "A" | "B" | "C"
  revenueShare: number
  cumulativeShare: number
}

export type ReturnItem = {
  article: string
  name: string
  nmId: number
  salesCount: number
  returnsCount: number
  returnRate: number
}

export type PriceVsSalesItem = {
  article: string
  name: string
  nmId: number
  avgPrice: number
  avgCost: number
  salesCount: number
  revenue: number
  profit: number
  margin: number
}

export type AnalyticsSummary = {
  totalRevenue: number
  totalPayout: number // gross — сумма ppvz_for_pay по строкам «Продажа»
  totalPayoutNet: number // net — продажи − возвраты + компенсации/корректировки
  totalReturnPayout: number
  totalCompensationPayout: number
  totalCost: number
  totalProfit: number
  netProfit: number
  avgMargin: number
  netMargin: number
  totalCommission: number
  totalLogistics: number
  totalStorage: number
  totalPenalty: number
  totalDeduction: number
  totalAcceptance: number
  totalAcquiring: number
  totalRebillLogistic: number
  totalAdditionalPayment: number
  totalPpvzReward: number
  totalCashbackCommission: number
  totalInstallmentCofinancing: number
  salesCount: number
  salesCountWithCost: number
  returnsCount: number
  returnRate: number
}

export type AnalyticsData = {
  summary: AnalyticsSummary
  dailyDynamics: DailyPoint[]
  abcAnalysis: AbcItem[]
  returnsAnalysis: ReturnItem[]
  priceVsSales: PriceVsSalesItem[]
}

type AnalyticsState = {
  data: AnalyticsData | null
  status: STATUS
  dateFrom: string
  dateTo: string
}

const initialState: AnalyticsState = {
  data: null,
  status: STATUS.SUCCESS,
  dateFrom: "",
  dateTo: "",
}

const analyticsSlice = createSlice({
  name: "analytics",
  initialState,
  reducers: {
    setAnalyticsDates: (
      state,
      action: PayloadAction<{ dateFrom: string; dateTo: string }>,
    ) => {
      state.dateFrom = action.payload.dateFrom
      state.dateTo = action.payload.dateTo
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAnalytics.pending, (state) => {
        state.status = STATUS.IN_PROGRESS
        state.data = null
      })
      .addCase(fetchAnalytics.fulfilled, (state, action) => {
        state.status = STATUS.SUCCESS
        state.data = action.payload
      })
      .addCase(fetchAnalytics.rejected, (state) => {
        state.status = STATUS.ERROR
      })
  },
})

export const fetchAnalytics = createAsyncThunk(
  "analytics/fetch",
  async ({
    dateFrom,
    dateTo,
    excludeNoCost,
  }: {
    dateFrom: string
    dateTo: string
    excludeNoCost: boolean
  }) => {
    const response = await BaseApi.getAnalytics(dateFrom, dateTo, excludeNoCost)
    return response.data.data as AnalyticsData
  },
)

export const selectAnalytics = (state: RootState) => state.analytics
export const { setAnalyticsDates } = analyticsSlice.actions
export default analyticsSlice.reducer
